import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Mail, Lock, User, ArrowRight, Loader2, Beaker, Eye, EyeOff } from "lucide-react";

export default function AuthPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const isLogin = location.pathname === "/login";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(name, email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md space-y-8">
          <Link to="/" className="inline-flex items-center gap-2 hover:opacity-80">
            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
              <Beaker className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">ElastoAI</span>
          </Link>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">{isLogin ? t("dashboard.welcome") : t("auth.register")}</h1>
              <p className="mt-2 text-slate-600">{isLogin ? "Sign in to your polymer & elastomer workspace" : "Start predicting elastomer formulations with AI"}</p>
            </div>
            <LanguageSwitcher className="mt-1" />
          </div>

          <form onSubmit={submit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label className="text-slate-700">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" required className="pl-10 h-12 bg-white border-slate-200" />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required className="pl-10 h-12 bg-white border-slate-200" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input data-testid="auth-password-input" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} className="pl-10 pr-12 h-12 bg-white border-slate-200" />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  data-testid="toggle-password-visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 rounded"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="auth-error">{error}</div>}

            <Button type="submit" disabled={loading} data-testid="auth-submit-btn" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full font-medium">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{isLogin ? "Sign In" : "Create Account"} <ArrowRight className="w-5 h-5 ml-2" /></>}
            </Button>
          </form>

          <p className="text-center text-slate-600 text-sm">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} data-testid="auth-toggle-link" className="text-orange-500 hover:text-orange-600 font-medium">
              {isLogin ? "Sign up" : "Sign in"}
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block bg-cover bg-center relative"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1746422125911-37dd7e925fa3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxtb2xlY3VsYXIlMjBzdHJ1Y3R1cmUlMjAzZCUyMGFic3RyYWN0JTIwYmx1ZXxlbnwwfHx8fDE3Njc4NjAyMjh8MA&ixlib=rb-4.1.0&q=85')` }}>
        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            <h2 className="font-heading text-2xl font-bold text-white mb-3">Precision Formulation Intelligence</h2>
            <p className="text-slate-200">Polymer & elastomer expertise grounded in your knowledge base. Strict, hybrid, or general modes — always cited, never hallucinated.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
